import React, { useCallback, createRef } from "react";
import PropTypes from "prop-types";
import classnames from "classnames";
import Checkbox from "components/atoms/Checkbox";

export const STYLE_TYPES = {
  FILTER_BOX: "filterbox",
};

const CheckboxGroup = ({
  name,
  styleType = STYLE_TYPES.FILTER_BOX,
  selectedValues = [],
  onValuesChange,
  checkboxes = [],
}) => {
  const handleCheckboxChange = useCallback(
    (e) => {
      const { checked, value } = e.target;
      const newSelectedValues = checked
        ? [...selectedValues, value]
        : selectedValues.filter((v) => v !== value);

      onValuesChange({ name, values: newSelectedValues });
    },
    [selectedValues, name, onValuesChange],
  );

  const handleKeyDown = useCallback((e, refs, index) => {
    let next;
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        next = refs[(index + 1) % refs.length];
        break;
      case "ArrowUp":
        e.preventDefault();
        next = refs[(index - 1 + refs.length) % refs.length];
        break;
    }
    if (next) {
      next.current?.focus();
    }
  }, []);

  const componentClassName = "sal-checkboxgroup";
  const classNames = classnames(
    componentClassName,
    `${componentClassName}--${styleType}`,
  );

  const checkboxRefs = checkboxes.map(() => createRef(null));

  const checkboxList = checkboxes.map((checkbox, index) => {
    const isActive = selectedValues.some((v) => v === checkbox.value);
    const itemClassName = `${componentClassName}__item`;
    const itemClassNames = classnames(itemClassName, {
      [`${itemClassName}--active`]: isActive,
    });

    return (
      <li key={`checkbox-${index}`} className={itemClassNames}>
        <Checkbox
          {...checkbox}
          name={name}
          checked={isActive}
          ref={checkboxRefs[index]}
          onChange={handleCheckboxChange}
          onKeyDown={(e) => handleKeyDown(e, checkboxRefs, index)}
        />
      </li>
    );
  });

  return (
    <ul className={classNames} role="group" aria-label={name}>
      {checkboxList}
    </ul>
  );
};

CheckboxGroup.propTypes = {
  name: PropTypes.string.isRequired,
  styleType: PropTypes.oneOf(
    Object.keys(STYLE_TYPES).map((k) => STYLE_TYPES[k]),
  ),
  selectedValues: PropTypes.array,
  onValuesChange: PropTypes.func,
  checkboxes: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
      label: PropTypes.node,
    }),
  ),
};

export default CheckboxGroup;
